/**
 * Shared looping alert sound preference for Tammy (Orders + all admin pages).
 * Persists in localStorage and syncs across tabs.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  isAdminSoundEnabled,
  LS_ADMIN_ORDERS_SOUND,
  setAdminSoundEnabled,
  stopAllAlertSoundLoops,
} from '../utils/adminNotifications';

interface AdminAlertSoundContextValue {
  /** When false, flash popups and browser alerts still work — only looping sound is muted. */
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  toggleSound: () => void;
}

const AdminAlertSoundContext = createContext<AdminAlertSoundContextValue | null>(null);

export function AdminAlertSoundProvider({ children }: { children: ReactNode }) {
  const [soundEnabled, setSoundEnabledState] = useState(() => isAdminSoundEnabled());

  const setSoundEnabled = useCallback((enabled: boolean) => {
    setAdminSoundEnabled(enabled);
    setSoundEnabledState(enabled);
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled(!soundEnabled);
  }, [setSoundEnabled, soundEnabled]);

  // Other tabs: keep toggle in sync.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== LS_ADMIN_ORDERS_SOUND || event.newValue == null) return;
      const enabled = event.newValue === '1';
      setSoundEnabledState(enabled);
      if (!enabled) stopAllAlertSoundLoops();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({ soundEnabled, setSoundEnabled, toggleSound }),
    [soundEnabled, setSoundEnabled, toggleSound],
  );

  return (
    <AdminAlertSoundContext.Provider value={value}>{children}</AdminAlertSoundContext.Provider>
  );
}

export function useAdminAlertSound() {
  const ctx = useContext(AdminAlertSoundContext);
  if (!ctx) {
    throw new Error('useAdminAlertSound must be used within AdminAlertSoundProvider');
  }
  return ctx;
}
